import React from "react";
import styles from "./LoyaltyProgramAnalysis.module.scss";
import { Card, Row, Col, Table, Select, Typography, Tag, Space, DatePicker, Progress } from "antd";
import {
  ArrowUpOutlined, ArrowDownOutlined, GiftOutlined, TeamOutlined, DollarOutlined,
  WarningOutlined, CalendarOutlined, PieChartOutlined, BarChartOutlined,
  BulbOutlined, RocketOutlined, AlertOutlined,
} from "@ant-design/icons";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement,
  ArcElement, Title as CTitle, Legend, Tooltip as CTooltip, Filler,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, CTitle, Legend, CTooltip, Filler);

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const fmt = (v: number) => `$${v.toLocaleString("es-AR", { maximumFractionDigits: 0 })}`;
const fmtN = (v: number) => v.toLocaleString("es-AR");
const fmtPts = (v: number) => v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(0)}K` : String(v);

/* ─── hardcoded data ─── */

const TOTAL_CLIENTES = 24800;
const CLIENTES_PROGRAMA = 15200;
const VENTA_TOTAL = 54400000;
const VENTA_FIDELIZADOS = 38100000;

const COMPRAS_FIDEL = 42600;
const COMPRAS_NO_FIDEL = 18400;
const CLIENTES_NO_FIDEL = TOTAL_CLIENTES - CLIENTES_PROGRAMA;

const TICKET_FIDEL = VENTA_FIDELIZADOS / COMPRAS_FIDEL;
const TICKET_NO_FIDEL = (VENTA_TOTAL - VENTA_FIDELIZADOS) / COMPRAS_NO_FIDEL;
const FREQ_FIDEL = COMPRAS_FIDEL / CLIENTES_PROGRAMA;
const FREQ_NO_FIDEL = COMPRAS_NO_FIDEL / CLIENTES_NO_FIDEL;

const PUNTOS_ACUM = 18400000;
const PUNTOS_CANJE = 7800000;
const PUNTOS_EXPIRADOS = 3900000;
const PUNTOS_ACTIVOS = PUNTOS_ACUM - PUNTOS_CANJE - PUNTOS_EXPIRADOS;
const VALOR_PUNTO = 0.5;

const tiers = [
  { key: 1, nivel: "Bronce", clientes: 8200, venta: 9800000, tickets: 14200 },
  { key: 2, nivel: "Plata", clientes: 4100, venta: 12400000, tickets: 14800 },
  { key: 3, nivel: "Oro", clientes: 2200, venta: 11200000, tickets: 9800 },
  { key: 4, nivel: "Platino", clientes: 700, venta: 4700000, tickets: 3800 },
];

const evolutionLabels = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
const evolutionData = {
  labels: evolutionLabels,
  datasets: [
    { label: "Clientes en programa", data: [12800, 13100, 13400, 13600, 13900, 14200, 14400, 14600, 14800, 15000, 15100, 15200], borderColor: "#8b5cf6", backgroundColor: "rgba(139,92,246,0.08)", fill: false, tension: 0.3, yAxisID: "y", pointRadius: 3, borderWidth: 2 },
    { label: "Puntos emitidos (K)", data: [1200, 1350, 1280, 1500, 1420, 1600, 1580, 1700, 1650, 1800, 1750, 1870], borderColor: "#faad14", backgroundColor: "transparent", fill: false, tension: 0.3, yAxisID: "y1", pointRadius: 3, borderWidth: 2, borderDash: [5, 3] },
    { label: "Puntos canjeados (K)", data: [480, 520, 510, 620, 580, 680, 700, 720, 690, 760, 740, 800], borderColor: "#52c41a", backgroundColor: "transparent", fill: false, tension: 0.3, yAxisID: "y1", pointRadius: 3, borderWidth: 2 },
    { label: "Venta fidelizados ($M)", data: [2.6, 2.8, 2.7, 3.1, 3.0, 3.4, 3.3, 3.5, 3.4, 3.6, 3.5, 3.7], borderColor: "#1890ff", backgroundColor: "rgba(24,144,255,0.06)", fill: true, tension: 0.3, yAxisID: "y2", pointRadius: 3, borderWidth: 2 },
  ],
};
const evolutionOpts = {
  responsive: true, maintainAspectRatio: false,
  plugins: { legend: { position: "top" as const, labels: { usePointStyle: true, font: { size: 11 } } } },
  scales: {
    y: { position: "left" as const, grid: { color: "rgba(0,0,0,0.04)" }, title: { display: true, text: "Clientes", font: { size: 10 } } },
    y1: { position: "right" as const, grid: { display: false }, title: { display: true, text: "Puntos (K)", font: { size: 10 } } },
    y2: { position: "right" as const, display: false },
    x: { grid: { display: false } },
  },
};

const redemptions = [
  { key: 1, recompensa: "Descuento 10% próxima compra", tipo: "Descuento", veces: 3200, ticket: 4800, margen: 38.2, retorno: "Alto" },
  { key: 2, recompensa: "Envío gratis", tipo: "Beneficio", veces: 2800, ticket: 3200, margen: 42.5, retorno: "Alto" },
  { key: 3, recompensa: "Producto gratis (medias)", tipo: "Producto", veces: 1400, ticket: 5200, margen: 35.8, retorno: "Medio" },
  { key: 4, recompensa: "2x1 accesorios", tipo: "Descuento", veces: 980, ticket: 2800, margen: 28.4, retorno: "Bajo" },
  { key: 5, recompensa: "Acceso VIP evento", tipo: "Experiencia", veces: 320, ticket: 8500, margen: 48.2, retorno: "Alto" },
  { key: 6, recompensa: "Upgrade de nivel temporal", tipo: "Beneficio", veces: 420, ticket: 6200, margen: 44.1, retorno: "Medio" },
];

const atRisk = [
  { ventana: "30 días", puntos: 1200000, clientes: 2800, valor: 1200000 * VALOR_PUNTO },
  { ventana: "60 días", puntos: 1800000, clientes: 3400, valor: 1800000 * VALOR_PUNTO },
  { ventana: "90 días", puntos: 2400000, clientes: 4100, valor: 2400000 * VALOR_PUNTO },
];

const expiryByMonth = [
  { key: 1, mes: "May 2026", puntos: 380000, clientes: 820 },
  { key: 2, mes: "Jun 2026", puntos: 420000, clientes: 890 },
  { key: 3, mes: "Jul 2026", puntos: 350000, clientes: 740 },
  { key: 4, mes: "Ago 2026", puntos: 510000, clientes: 1020 },
  { key: 5, mes: "Sep 2026", puntos: 480000, clientes: 950 },
  { key: 6, mes: "Oct 2026", puntos: 390000, clientes: 810 },
  { key: 7, mes: "Nov 2026", puntos: 620000, clientes: 1180 },
  { key: 8, mes: "Dic 2026", puntos: 580000, clientes: 1100 },
  { key: 9, mes: "Ene 2027", puntos: 440000, clientes: 880 },
  { key: 10, mes: "Feb 2027", puntos: 360000, clientes: 720 },
  { key: 11, mes: "Mar 2027", puntos: 410000, clientes: 840 },
  { key: 12, mes: "Abr 2027", puntos: 460000, clientes: 920 },
];

const mixCanjesData = {
  labels: ["Descuento", "Producto", "Beneficio", "Experiencia"],
  datasets: [{
    data: [4180, 1400, 3220, 320],
    backgroundColor: ["#8b5cf6", "#faad14", "#52c41a", "#1890ff"],
    borderWidth: 0,
  }],
};
const mixCanjesOpts = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: "right" as const, labels: { usePointStyle: true, font: { size: 12 } } } } };

const comparison = [
  { key: 1, metrica: "Ticket promedio", fidel: TICKET_FIDEL, noFidel: TICKET_NO_FIDEL, format: "currency" },
  { key: 2, metrica: "Frecuencia (compras/cliente)", fidel: FREQ_FIDEL, noFidel: FREQ_NO_FIDEL, format: "decimal" },
  { key: 3, metrica: "Venta anual por cliente", fidel: VENTA_FIDELIZADOS / CLIENTES_PROGRAMA, noFidel: (VENTA_TOTAL - VENTA_FIDELIZADOS) / CLIENTES_NO_FIDEL, format: "currency" },
  { key: 4, metrica: "Recurrencia (%)", fidel: 68.4, noFidel: 32.1, format: "pct" },
];

const compBarData = {
  labels: ["Ticket promedio", "Frecuencia", "Venta anual/cliente", "Recurrencia (%)"],
  datasets: [
    { label: "Fidelizados", data: [TICKET_FIDEL, FREQ_FIDEL, VENTA_FIDELIZADOS / CLIENTES_PROGRAMA, 68.4], backgroundColor: "#8b5cf6" },
    { label: "No fidelizados", data: [TICKET_NO_FIDEL, FREQ_NO_FIDEL, (VENTA_TOTAL - VENTA_FIDELIZADOS) / CLIENTES_NO_FIDEL, 32.1], backgroundColor: "#d9d9d9" },
  ],
};
const compBarOpts = {
  responsive: true, maintainAspectRatio: false, indexAxis: "y" as const,
  plugins: { legend: { position: "top" as const, labels: { usePointStyle: true, font: { size: 11 } } } },
  scales: { x: { grid: { color: "rgba(0,0,0,0.04)" } }, y: { grid: { display: false } } },
};

const insights = [
  "Pendiente: implementar motor de insights automáticos del programa de fidelidad.",
];

const actions = [
  { label: "Campaña por puntos a vencer", description: "Activar clientes con puntos próximos a expirar en 30 días." },
  { label: "Optimización de recompensas", description: "Revisar recompensas con bajo retorno y reemplazar por alternativas más efectivas." },
  { label: "Revisión de progresión de niveles", description: "Evaluar umbrales entre niveles para facilitar la maduración de clientes Bronce." },
];

const LoyaltyProgramAnalysis: React.FC = () => {
  const pctBase = (CLIENTES_PROGRAMA / TOTAL_CLIENTES) * 100;
  const pctVenta = (VENTA_FIDELIZADOS / VENTA_TOTAL) * 100;
  const freqRatio = FREQ_FIDEL / FREQ_NO_FIDEL;
  const ticketRatio = TICKET_FIDEL / TICKET_NO_FIDEL;
  const pctCanje = (PUNTOS_CANJE / PUNTOS_ACUM) * 100;
  const breakage = (PUNTOS_EXPIRADOS / PUNTOS_ACUM) * 100;

  const fmtComp = (v: number, f: string) => {
    if (f === "currency") return fmt(Math.round(v));
    if (f === "pct") return `${v.toFixed(1)}%`;
    return v.toFixed(2);
  };

  return (
    <div className={styles.wrapper}>
      <Title level={3} className={styles.pageTitle}>Programa de Fidelidad</Title>

      {/* FILTERS */}
      <Card className={styles.filtersCard} size="small">
        <div className={styles.filtersRow}>
          <div className={styles.filterItem}><Text className={styles.filterLabel}>Período</Text><RangePicker className={styles.filterInput} /></div>
          <div className={styles.filterItem}><Text className={styles.filterLabel}>Canal</Text><Select placeholder="Todos" allowClear className={styles.filterSelect} options={[{ value: "retail", label: "Retail" }, { value: "ecommerce", label: "eCommerce" }, { value: "mkt", label: "Marketplace" }]} /></div>
          <div className={styles.filterItem}><Text className={styles.filterLabel}>Nivel</Text><Select placeholder="Todos" allowClear className={styles.filterSelect} options={[{ value: "bronce", label: "Bronce" }, { value: "plata", label: "Plata" }, { value: "oro", label: "Oro" }, { value: "platino", label: "Platino" }]} /></div>
        </div>
      </Card>

      {/* KPIs */}
      <div className={styles.kpiRow}>
        {[
          { label: "Clientes en programa", value: `${pctBase.toFixed(1)}%`, sub: `${fmtN(CLIENTES_PROGRAMA)} de ${fmtN(TOTAL_CLIENTES)}` },
          { label: "Venta fidelizados", value: `${pctVenta.toFixed(1)}%`, sub: fmt(VENTA_FIDELIZADOS) },
          { label: "Frecuencia (ratio)", value: `${freqRatio.toFixed(1)}x`, sub: `${FREQ_FIDEL.toFixed(1)} vs ${FREQ_NO_FIDEL.toFixed(1)}` },
          { label: "Ticket (ratio)", value: `${ticketRatio.toFixed(1)}x`, sub: `${fmt(Math.round(TICKET_FIDEL))} vs ${fmt(Math.round(TICKET_NO_FIDEL))}` },
          { label: "% canje de puntos", value: `${pctCanje.toFixed(1)}%`, sub: `${fmtPts(PUNTOS_CANJE)} de ${fmtPts(PUNTOS_ACUM)}` },
          { label: "Breakage", value: `${breakage.toFixed(1)}%`, sub: `${fmtPts(PUNTOS_EXPIRADOS)} expirados` },
        ].map((k, i) => (
          <Card className={styles.kpiCard} key={i}>
            <Text className={styles.kpiLabel}>{k.label}</Text>
            <div className={styles.kpiValue}>{k.value}</div>
            <Text className={styles.kpiSub}>{k.sub}</Text>
          </Card>
        ))}
      </div>

      {/* ECONOMY */}
      <Card className={styles.sectionCard} title={<Space><DollarOutlined /><span>Economía del programa (pasivo)</span></Space>}>
        <div className={styles.economyRow}>
          {[
            { label: "Puntos acumulados", value: fmtPts(PUNTOS_ACUM), color: "#8b5cf6" },
            { label: "Puntos canjeados", value: fmtPts(PUNTOS_CANJE), color: "#52c41a" },
            { label: "Puntos expirados", value: fmtPts(PUNTOS_EXPIRADOS), color: "#faad14" },
            { label: "Puntos activos (pasivo)", value: fmtPts(PUNTOS_ACTIVOS), color: "#f5222d", isCritical: true },
          ].map((e, i) => (
            <div key={i} className={`${styles.economyBlock} ${e.isCritical ? styles.critical : ""}`}>
              <Text className={styles.economyLabel}>{e.label}</Text>
              <div className={styles.economyValue} style={{ color: e.color }}>{e.value}</div>
              {e.isCritical && <Text type="secondary" style={{ fontSize: 11 }}>≈ {fmt(PUNTOS_ACTIVOS * VALOR_PUNTO)} en pasivo</Text>}
            </div>
          ))}
        </div>
      </Card>

      {/* TIER DISTRIBUTION */}
      <Card className={styles.sectionCard} title={<Space><TeamOutlined /><span>Distribución de clientes por nivel</span></Space>}>
        <Table columns={[
          { title: "Nivel", dataIndex: "nivel", key: "nivel", render: (v: string) => <Tag color={v === "Platino" ? "purple" : v === "Oro" ? "gold" : v === "Plata" ? "blue" : "default"}>{v}</Tag> },
          { title: "Clientes", dataIndex: "clientes", key: "clientes", render: (v: number) => fmtN(v), align: "right" as const },
          { title: "% base", key: "pctBase", render: (_: any, r: any) => `${((r.clientes / CLIENTES_PROGRAMA) * 100).toFixed(1)}%`, align: "right" as const },
          { title: "Venta", dataIndex: "venta", key: "venta", render: (v: number) => fmt(v), align: "right" as const },
          { title: "Ticket", key: "ticket", render: (_: any, r: any) => fmt(Math.round(r.venta / r.tickets)), align: "right" as const },
          { title: "Frecuencia", key: "freq", render: (_: any, r: any) => (r.tickets / r.clientes).toFixed(1), align: "right" as const },
        ]} dataSource={tiers} pagination={false} size="small" className={styles.dataTable} />
      </Card>

      {/* EVOLUTION CHART */}
      <Card className={styles.sectionCard} title={<Space><CalendarOutlined /><span>Evolución del programa en el tiempo</span></Space>}>
        <div className={styles.chartContainer}><Line data={evolutionData} options={evolutionOpts as any} /></div>
      </Card>

      {/* REDEMPTION BEHAVIOR */}
      <Card className={styles.sectionCard} title={<Space><GiftOutlined /><span>Comportamiento de canje</span></Space>}>
        <Table columns={[
          { title: "Recompensa", dataIndex: "recompensa", key: "recompensa" },
          { title: "Tipo", dataIndex: "tipo", key: "tipo", render: (v: string) => <Tag>{v}</Tag> },
          { title: "Veces canjeada", dataIndex: "veces", key: "veces", render: (v: number) => fmtN(v), align: "right" as const },
          { title: "Ticket asociado", dataIndex: "ticket", key: "ticket", render: (v: number) => fmt(v), align: "right" as const },
          { title: "Margen", dataIndex: "margen", key: "margen", render: (v: number) => `${v.toFixed(1)}%`, align: "right" as const },
          { title: "Retorno", dataIndex: "retorno", key: "retorno", render: (v: string) => <Tag color={v === "Alto" ? "green" : v === "Medio" ? "gold" : "red"}>{v}</Tag> },
        ]} dataSource={redemptions} pagination={false} size="small" className={styles.dataTable} />
      </Card>

      {/* AT-RISK POINTS + EXPIRY */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={10}>
          <Card className={styles.sectionCard} title={<Space><WarningOutlined style={{ color: "#f5222d" }} /><span>Puntos en riesgo</span></Space>}>
            <div className={styles.riskBlocks}>
              {atRisk.map((r, i) => (
                <div key={i} className={styles.riskBlock}>
                  <div className={styles.riskHeader}><Tag color={i === 0 ? "red" : i === 1 ? "orange" : "gold"}>{r.ventana}</Tag></div>
                  <div className={styles.riskRow}><Text type="secondary">Puntos</Text><Text strong>{fmtPts(r.puntos)}</Text></div>
                  <div className={styles.riskRow}><Text type="secondary">Clientes</Text><Text strong>{fmtN(r.clientes)}</Text></div>
                  <div className={styles.riskRow}><Text type="secondary">Valor estimado</Text><Text strong style={{ color: "#f5222d" }}>{fmt(r.valor)}</Text></div>
                </div>
              ))}
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={14}>
          <Card className={styles.sectionCard} title={<Space><CalendarOutlined /><span>Desglose de vencimientos (12 meses)</span></Space>}>
            <Table columns={[
              { title: "Mes", dataIndex: "mes", key: "mes" },
              { title: "Puntos a vencer", dataIndex: "puntos", key: "puntos", render: (v: number) => fmtPts(v), align: "right" as const },
              { title: "Clientes afectados", dataIndex: "clientes", key: "clientes", render: (v: number) => fmtN(v), align: "right" as const },
            ]} dataSource={expiryByMonth} pagination={false} size="small" scroll={{ y: 320 }} className={styles.dataTable} />
          </Card>
        </Col>
      </Row>

      {/* MIX CANJES + COMPARISON */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={8}>
          <Card className={styles.sectionCard} title={<Space><PieChartOutlined /><span>Mix de canjes por tipo</span></Space>}>
            <div className={styles.chartContainerSmall}><Doughnut data={mixCanjesData} options={mixCanjesOpts} /></div>
          </Card>
        </Col>
        <Col xs={24} lg={16}>
          <Card className={styles.sectionCard} title={<Space><BarChartOutlined /><span>Comparativa fidelizados vs no fidelizados</span></Space>}>
            <Table columns={[
              { title: "Métrica", dataIndex: "metrica", key: "metrica" },
              { title: "Fidelizados", key: "fidel", render: (_: any, r: any) => <Text strong>{fmtComp(r.fidel, r.format)}</Text>, align: "right" as const },
              { title: "No fidelizados", key: "noFidel", render: (_: any, r: any) => fmtComp(r.noFidel, r.format), align: "right" as const },
              {
                title: "Brecha", key: "brecha",
                render: (_: any, r: any) => {
                  const diff = r.format === "pct" ? r.fidel - r.noFidel : ((r.fidel - r.noFidel) / r.noFidel) * 100;
                  const pos = diff >= 0;
                  return (
                    <span className={`${styles.variation} ${pos ? styles.positive : styles.negative}`}>
                      {pos ? <ArrowUpOutlined /> : <ArrowDownOutlined />} {pos ? "+" : ""}{diff.toFixed(1)}{r.format === "pct" ? "pp" : "%"}
                    </span>
                  );
                },
                align: "right" as const,
              },
            ]} dataSource={comparison} pagination={false} size="small" className={styles.dataTable} />
          </Card>
        </Col>
      </Row>

      {/* COMPARISON BAR CHART */}
      <Card className={styles.sectionCard} title={<Space><BarChartOutlined /><span>Comparativa visual</span></Space>}>
        <div className={styles.chartContainer}><Bar data={compBarData} options={compBarOpts as any} /></div>
      </Card>

      {/* INSIGHTS + ACTIONS */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={12}>
          <Card className={styles.sectionCard} title={<Space><BulbOutlined style={{ color: "#faad14" }} /><span>Insights automáticos</span></Space>}>
            <div className={styles.bulletList}>
              {insights.map((ins, i) => (
                <div key={i} className={styles.bulletItem}>
                  <BulbOutlined className={styles.bYellow} />
                  <Text type="secondary">{ins}</Text>
                </div>
              ))}
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card className={styles.sectionCard} title={<Space><RocketOutlined style={{ color: "#1890ff" }} /><span>Acciones sugeridas</span></Space>}>
            <div className={styles.actionsList}>
              {actions.map((a, i) => (
                <div key={i} className={styles.actionItem}>
                  <Text strong>{a.label}</Text>
                  <Text type="secondary" className={styles.actionDesc}>{a.description}</Text>
                </div>
              ))}
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default LoyaltyProgramAnalysis;
